import React from 'react';

import http from 'api/http';
import { trailerAPI } from 'api/requests';

import { APPLICATION_MODE } from 'constants/trailer';
import { Input } from 'Components/Input';
import Checkbox from 'Components/Checkbox';
import Button, { buttonColor, buttonType } from 'Components/Button';
import NotificationManager from 'dom-managers/notification';
import Select from 'Components/Select';
import Info from 'Components/Info';

class Application extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      submitInProcess: false,
      invalidFields: {},
      modelYears: []
    };

    this.showErrors = false;
  }

  componentDidMount() {
    http.get(trailerAPI.getPermissibleModelYears)
      .then(({data}) => {
        const modelYears = data.sort();
        this.setState({modelYears});
      });
  }

  componentDidUpdate() {
    const {selectedTrailer} = this.props;

    this._checkRequiredFields(selectedTrailer)
      .catch(() => {});
  }


  _toggleAnimation = () => {
    const {applicationMode} = this.props;

    switch (applicationMode) {
      case APPLICATION_MODE.OPENED:
        return '_top_105';
      case APPLICATION_MODE.CLOSED:
        return '_top_negative_985';
      case APPLICATION_MODE.OPENING:
        return '_translate_bottom';
      case APPLICATION_MODE.CLOSING:
        return '_translate_top';
      default:
        return '';
    }
  };

  _completeCheck = closeApplication => {
    const {selectTrailer} = this.props;

    this.showErrors = false;
    this.setState({invalidFields: {}}, () => {if (closeApplication) selectTrailer(null)()});
  };

  _handleSubmit = () => {
    const {submitInProcess} = this.state;
    const {updateList, selectedTrailer} = this.props;

    if (submitInProcess) return null;

    const data = this._removeFieldsForRequestBody(selectedTrailer);

    const showSubmitMessage = () => {
      if (this._hasInputErrors()) {
        NotificationManager.success(1, 'Your application was saved in Incomplete status.');
      } else {
        NotificationManager.success(1, 'Your application was submitted and approved successfully.');
      }
    };

    this.showErrors = true;
    this._checkRequiredFields(data)
      .then(() => {
        this.setState({submitInProcess: true}, () => {
          http.post(trailerAPI.trailers, data)
            .then(({data}) => {
              showSubmitMessage();
              updateList(data);
              this._completeCheck(selectedTrailer.id);
            })
            .finally(() => {
              this.setState({submitInProcess: false});
            });
        });
      })
      .catch(() => {
        NotificationManager.error(1, 'Please check the required fields.');
        this.forceUpdate();
      });
  };

  _removeFieldsForRequestBody = data => {
    data = {...data};

    if (!data.id) {
      delete data.id;
    }

    delete data.status;
    delete data.submitInProcess;
    delete data.user;
    delete data.submittedOn;
    delete data.primaryAccount;

    return data;
  };

  _checkTrailerInput = () => {
    const {selectedTrailer} = this.props;

    let couplerRatingWithBrakes = false;
    let couplerRatingWithoutBrakes = false;

    if (selectedTrailer.couplerRatingWithBrakes !== '' || selectedTrailer.couplerRatingWithoutBrakes !== '') {
      if (+selectedTrailer.couplerRatingWithBrakes === 0 && +selectedTrailer.couplerRatingWithoutBrakes === 0) {
        couplerRatingWithBrakes = true;
        couplerRatingWithoutBrakes = true;
      } else {
        if (+selectedTrailer.couplerRatingWithBrakes !== 0) {
          couplerRatingWithBrakes = +selectedTrailer.couplerRatingWithBrakes < +selectedTrailer.grossVehicleWeightRating;
          couplerRatingWithoutBrakes = false;
        } else if (+selectedTrailer.couplerRatingWithoutBrakes) {
          couplerRatingWithBrakes = false;
          couplerRatingWithoutBrakes = +selectedTrailer.couplerRatingWithoutBrakes < +selectedTrailer.grossVehicleWeightRating;
        }
      }
    }

    return {
      grossVehicleWeightRating: +selectedTrailer.grossVehicleWeightRating > +selectedTrailer.grossAxleWeightRating * +selectedTrailer.numberOfAxles,
      couplerRatingWithBrakes,
      couplerRatingWithoutBrakes,
      strapRating: +selectedTrailer.strapRating < 1.5 * +selectedTrailer.winchRating,
      hookRating: +selectedTrailer.hookRating < 1.5 * +selectedTrailer.strapRating,
      maxLoadCapacity: +selectedTrailer.maxLoadCapacity > +selectedTrailer.grossVehicleWeightRating - +selectedTrailer.emptyTrailerWeight,
    };
  };

  _isError = field => {
    const {invalidFields} = this.state;

    return (this.showErrors && invalidFields[field]) || this._checkTrailerInput()[field];
  };

  _hasInputErrors = () => {
    const {invalidFields} = this.state;
    const errors = this._checkTrailerInput();

    if (Object.keys(invalidFields).length) return true;

    return Object.keys(errors).find(errorKey => errors[errorKey]);
  };

  _getSubmitButtonLabel = () => {
    if (this._hasInputErrors()) {
      return 'Save Incomplete';
    }
    return 'Submit';
  };

  _checkRequiredFields = data => {
    const {invalidFields} = this.state;

    let isInvalidListChanged = false;

    data = {...data};

    delete data.id;
    delete data.status;
    delete data.submitInProcess;
    delete data.user;
    delete data.submittedOn;

    return new Promise((resolve, reject) => {
      for (let key in data) {
        if (data[key].toString() === '') {
          if (!invalidFields[key]) {
            invalidFields[key] = true;
            isInvalidListChanged = true;
          }
        }
        else {
          if (invalidFields[key]) {
            delete invalidFields[key];
            isInvalidListChanged = true;
          }
        }
      }

      if (isInvalidListChanged) {
        this.setState({invalidFields}, () => {
          if (Object.keys(invalidFields).length) {
            reject();
          } else {
            resolve();
          }
        });
      } else {
        if (Object.keys(invalidFields).length) {
          reject();
        } else {
          resolve();
        }
      }
    });
  };

  _getTrailerReadableStatus = () => {
    const {selectedTrailer} = this.props;

    if (selectedTrailer && selectedTrailer.status) {
      return selectedTrailer.status[0].toUpperCase() + selectedTrailer.status.slice(1).toLowerCase();
    }
    return '';
  };

  _isYearEditable = () => {
    const {selectedTrailer} = this.props;

    return !selectedTrailer.status;
  };

  render() {
    const {submitInProcess, modelYears} = this.state;
    const {selectedTrailer, handleCheckboxChange, handleInputChange} = this.props;

    return (
      <aside className={`scene__new-trailer-window-wrapper ${this._toggleAnimation()}`}>
        <div className='scene__new-trailer-window'>
          <header className='ect-header ect-header_size_s _border_bottom_1 _position_relative _height_100'>
            <div className='ect-header__text _color_dark_blue'>Trailer Application</div>
            {selectedTrailer.id && (
              <div className='ect-application__id _font_size_16 _color_gray _font_weight_300 _padding_left_135'>
                ID {selectedTrailer.id}
              </div>
            )}
            {selectedTrailer.status && (
              <div
                className={`__status __status_${selectedTrailer.status.toLowerCase()}
              __status_application _width_150 _height_40 _border_radius_20 _position_absolute _right_120 _bottom_negative_20 _color_white`}
              >
                {this._getTrailerReadableStatus()}
              </div>
            )}
          </header>

          <section className='ect-body _flex _padding_horizontal_90 _padding_top_40 _padding_bottom_20 _border_bottom_1 _flex_wrap'>
            <div className='_width_pc_30 _padding_right_20 _box-sizing_border-box _min_width_240'>
              <Input
                label='Trailer Name'
                mainWrapperClassName='_margin_bottom_24'
                labelClassName='_font_size_10 _min_height_26 _block _min_height_26 _block'
                inputClassName='_height_30 _border_radius_6 _width_pc_100'
                value={selectedTrailer.name}
                error={this._isError('name')}
                tabIndex={1}
                onChange={handleInputChange('name')}
              />
              <div className='_flex _ai_center _jc_between'>
                <Checkbox label='Over 80" Wide' value={selectedTrailer.over80Wide} onChange={handleCheckboxChange} />
                <Input
                  label='Rope/Strap Rating'
                  mainWrapperClassName='_margin_bottom_24'
                  labelClassName='_font_size_10 _min_height_26 _block'
                  inputClassName='_height_30 _border_radius_6 _width_pc_100'
                  isNumeric
                  value={selectedTrailer.strapRating}
                  error={this._isError('strapRating')}
                  tabIndex={7}
                  onChange={handleInputChange('strapRating')}
                >
                  <Info
                    triggerElement={<div className='__info-icon' />}
                    popperElement={<div className='info__tooltip-body'>Rope Rating should be ≥ 1.5 * Winch Rating</div>}
                    triggerWrapperClassName='_position_absolute _top_8 _right_7 _z_index_200'
                  />
                </Input>
              </div>
            </div>
            <div className='_width_pc_14 _padding_horizontal_20 _box-sizing_border-box _min_width_120'>
              <Select
                label='Model Year'
                mainWrapperClassName='_width_pc_100 _margin_bottom_4 _margin_top_0'
                labelClassName='_font_size_10 _min_height_26 _block'
                inputClassName='_height_30 _border_radius_6 _margin_top_0 _margin_bottom_20'
                dropDownClassName='_border_radius_bottom_left_6 _border_radius_bottom_right_6 _top_55'
                top={56}
                disabled={!this._isYearEditable()}
                defaultItem={selectedTrailer.year.toString()}
                error={this._isError('year')}
                selectionList={modelYears}
                tabIndex={2}
                isSlave
                withoutSearch
                handleSelect={handleInputChange('year')}
              />
              <Input
                label='Axles'
                mainWrapperClassName='_margin_bottom_24'
                labelClassName='_font_size_10 _min_height_26 _block'
                inputClassName='_height_30 _border_radius_6 _width_pc_100'
                isNumeric
                digitsAfterDot={0}
                value={selectedTrailer.numberOfAxles}
                error={this._isError('numberOfAxles')}
                tabIndex={8}
                onChange={handleInputChange('numberOfAxles')}
              />
            </div>
            <div className='_width_pc_14 _padding_horizontal_20 _box-sizing_border-box _min_width_120'>
              <Input
                label='Empty Trailer Weight'
                mainWrapperClassName='_margin_bottom_24'
                labelClassName='_font_size_10 _min_height_26 _block'
                inputClassName='_height_30 _border_radius_6 _width_pc_100'
                isNumeric
                value={selectedTrailer.emptyTrailerWeight}
                error={this._isError('emptyTrailerWeight')}
                tabIndex={3}
                onChange={handleInputChange('emptyTrailerWeight')}
              />
              <Input
                label='Winch Rating'
                mainWrapperClassName='_margin_bottom_24'
                labelClassName='_font_size_10 _min_height_26 _block'
                inputClassName='_height_30 _border_radius_6 _width_pc_100'
                isNumeric
                value={selectedTrailer.winchRating}
                error={this._isError('winchRating')}
                tabIndex={9}
                onChange={handleInputChange('winchRating')}
              />
            </div>
            <div className='_width_pc_14 _padding_horizontal_20 _box-sizing_border-box _min_width_120'>
              <Input
                label='Coupler Rating w/o Brakes'
                mainWrapperClassName='_margin_bottom_24'
                labelClassName='_font_size_10 _min_height_26 _block'
                inputClassName='_height_30 _border_radius_6 _width_pc_100'
                isNumeric
                value={selectedTrailer.couplerRatingWithoutBrakes}
                error={this._isError('couplerRatingWithoutBrakes')}
                tabIndex={4}
                onChange={handleInputChange('couplerRatingWithoutBrakes')}
              >
                <Info
                  triggerElement={<div className='__info-icon' />}
                  popperElement={
                    <div className='info__tooltip-body'>
                      Coupler Rating w/o Brakes should be ≥ GVWR if it is {'>'} 0 and Coupler Rating w/Brakes = 0 (Coupler
                      Rating w/o Brakes may be equal to 0 if Coupler Rating w/Brakes {'>'} 0).
                    </div>
                  }
                  triggerWrapperClassName='_position_absolute _top_8 _right_7 _z_index_200'
                />
              </Input>
              <Input
                label='GAWR'
                mainWrapperClassName='_margin_bottom_24'
                labelClassName='_font_size_10 _min_height_26 _block'
                inputClassName='_height_30 _border_radius_6 _width_pc_100'
                isNumeric
                value={selectedTrailer.grossAxleWeightRating}
                error={this._isError('grossAxleWeightRating')}
                tabIndex={10}
                onChange={handleInputChange('grossAxleWeightRating')}
              />
            </div>
            <div className='_width_pc_14 _padding_horizontal_20 _box-sizing_border-box _min_width_120'>
              <Input
                label='Coupler Rating w/Brakes'
                mainWrapperClassName='_margin_bottom_24'
                labelClassName='_font_size_10 _min_height_26 _block'
                inputClassName='_height_30 _border_radius_6 _width_pc_100'
                isNumeric
                value={selectedTrailer.couplerRatingWithBrakes}
                error={this._isError('couplerRatingWithBrakes')}
                tabIndex={5}
                onChange={handleInputChange('couplerRatingWithBrakes')}
              >
                <Info
                  triggerElement={<div className='__info-icon' />}
                  popperElement={
                    <div className='info__tooltip-body'>
                      Coupler Rating w/Brakes should be ≥ GVWR if it is {'>'} 0 (Coupler Rating w/Brakes may be equal to 0
                      if Coupler Rating w/o Brakes {'>'} 0).
                    </div>
                  }
                  triggerWrapperClassName='_position_absolute _top_8 _right_7 _z_index_200'
                />
              </Input>
              <Input
                label='GVWR'
                mainWrapperClassName='_margin_bottom_24'
                labelClassName='_font_size_10 _min_height_26 _block'
                inputClassName='_height_30 _border_radius_6 _width_pc_100'
                isNumeric
                value={selectedTrailer.grossVehicleWeightRating}
                error={this._isError('grossVehicleWeightRating')}
                tabIndex={11}
                onChange={handleInputChange('grossVehicleWeightRating')}
              >
                <Info
                  triggerElement={<div className='__info-icon' />}
                  popperElement={<div className='info__tooltip-body'>GVWR should be ≤ GAWR * Number of Axles</div>}
                  triggerWrapperClassName='_position_absolute _top_8 _right_7 _z_index_200'
                />
              </Input>
            </div>
            <div className='_width_pc_14 _padding_left_20 _box-sizing_border-box _min_width_120'>
              <Input
                label='Hook Rating'
                mainWrapperClassName='_margin_bottom_24'
                labelClassName='_font_size_10 _min_height_26 _block'
                inputClassName='_height_30 _border_radius_6 _width_pc_100'
                isNumeric
                value={selectedTrailer.hookRating}
                error={this._isError('hookRating')}
                tabIndex={6}
                onChange={handleInputChange('hookRating')}
              >
                <Info
                  triggerElement={<div className='__info-icon' />}
                  popperElement={<div className='info__tooltip-body'>Hook Rating should be ≥ 1.5 * Rope Rating</div>}
                  triggerWrapperClassName='_position_absolute _top_8 _right_7 _z_index_200'
                />
              </Input>
              <Input
                label='Max Load Capacity'
                mainWrapperClassName='_margin_bottom_24'
                labelClassName='_font_size_10 _min_height_26 _block'
                inputClassName='_height_30 _border_radius_6 _width_pc_100'
                isNumeric
                value={selectedTrailer.maxLoadCapacity}
                error={this._isError('maxLoadCapacity')}
                tabIndex={12}
                onChange={handleInputChange('maxLoadCapacity')}
              >
                <Info
                  triggerElement={<div className='__info-icon' />}
                  popperElement={
                    <div className='info__tooltip-body'>
                      Maximum Load Capacity should be ≤ (Standard GVWR - Empty Trailer Weight)
                    </div>
                  }
                  triggerWrapperClassName='_position_absolute _top_8 _right_7 _z_index_200'
                />
              </Input>
            </div>
          </section>

          <section className='ect-footer _flex _ai_center _jc_end _padding_horizontal_120 _padding_vertical_20'>
            <Button
              label='Cancel'
              type={buttonType.secondary}
              color={buttonColor.gray}
              buttonClassName='_height_40 _width_120'
              onClick={this._completeCheck}
            />
            <Button
              label={this._getSubmitButtonLabel()}
              disabledWithSpinner={submitInProcess}
              onClick={this._handleSubmit}
              buttonClassName='_height_40 _width_160'
            />
          </section>
        </div>
      </aside>
    );
  }
}

export default Application;

Application.defaultProps = {
  selectedTrailer: {
    id: '',
    name: '',
    year: '',
    emptyTrailerWeight: '',
    over80Wide: false,
    couplerRatingWithBrakes: '',
    couplerRatingWithoutBrakes: '',
    strapRating: '',
    hookRating: '',
    winchRating: '',
    numberOfAxles: '',
    grossAxleWeightRating: '',
    grossVehicleWeightRating: '',
    maxLoadCapacity: '',
    status: ''
  }
};



// WEBPACK FOOTER //
// ./src/app/Scenes/TrailerManufacturer/Application/index.jsx